import React, { Component, createRef } from "react";
import { StyleSheet, View, TextInput, Text, TouchableOpacity, Platform, InputAccessoryView, Alert } from "react-native";
import { isMobileWeb, getNoAutofillProps, noAutofillRef, scannerFieldRef } from "../../utils/mobileWeb";
import { connect } from "react-redux";
import { searchItems } from "../../actions/records";
import { getMyCustomers, getMySuppliers } from "../../actions/submissions";
import DropDownLocations from "../widgets/dropdownLocations";
import colors from "../../constants/colors";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import Swal from "sweetalert2";
import BarcodeScannerComponent from "../widgets/BarCodeScannerComponent";
import KeyboardBar from "../widgets/KeyboardBar";
import { inputFocus } from "../../utils";
import {
    findLocationInList,
    locationListIncludesTerm,
    parseScannedValue,
    renderLocationLabel,
    getTextInputDomNode,
    cleanScannedText,
    normalizeWedgeScanText,
    startSymbolWedgeSession,
    stopSymbolWedgeSessionForOwner,
    extendSymbolScanEnterShield,
    isGarbledWedgeScanText,
} from "../../utils/locationScan";
import { KeyboardAvoidingView } from "react-native-web";

const BLUR_RESOLVE_MS = 250;
const WEDGE_IDLE_MS = 800;
// Delay after the scanner's trailing Enter before committing the scan value.
const WEDGE_ENTER_MS = 120;
const WEDGE_MANUAL_KB_EVENT = "gohaccp-wedge-manual-keyboard";
const WEDGE_FOCUS_GUARD_MS = 12000;
/** Pause before resolving a manually typed location (keyboard). */
const MANUAL_TYPE_DEBOUNCE_MS = 1500;
/** Pause before search/filter on non-scanner location fields. */
const TYPE_SEARCH_DEBOUNCE_MS = 600;
/** Fast resolve for hardware wedge / scanner keyboard only. */
const SCAN_RESOLVE_DELAY_MS = 120;

class SearchInventoryLocations extends Component {
    inputRef = createRef();
    _mounted = false;
    _handlingScan = false;

    constructor(props) {
        super(props);
        this._interval = null;
        this._resolveTimer = null;
        const useScanner = (
            (props.isShipmentByLocation && props.showLocationsSearch && props.defaultLabel === "location") ||
            (props.isInventoryTransfer && (props.defaultLabel === "fromLocation" || props.defaultLabel === "toLocation"))
        );
        this.state = {
            value: (props.defaultLabel == "toLocation" || props.showLocationsSearch) ? renderLocationLabel(props.initial) : typeof props.initial == "string" ? props.initial : "",
            locationSelected: props.initial && typeof props.initial == "object" ? props.initial : [],
            height: 30,
            items: props.options ? props.options : [],
            filtered: props.options ? props.options : [],
            wasEdited: false,
            openList: false,
            scan: false,
            scanerCanceled: false,
            softFocus: useScanner && props.scannerInput && Platform.OS === "ios" ? false : true,
            inputAccessoryViewID: useScanner ? `location-${props.id}-${props.position}` : undefined,
        };
    }
    componentDidMount() {
        this._mounted = true;
        this.attachDomScanListeners();
        if (this.usesWebHardwareScanner() && typeof window !== "undefined") {
            this._onWedgeManualKeyboard = (e) => {
                const { id, position } = e?.detail || {};
                if (id === this.props.id && position === this.props.position) {
                    this.enableManualKeyboard();
                }
            };
            window.addEventListener(WEDGE_MANUAL_KB_EVENT, this._onWedgeManualKeyboard);
        }
    }

    isEmptyInitial(initial) {
        if (initial == null || initial === "") return true;
        if (Array.isArray(initial) && initial.length === 0) return true;
        return false;
    }

    getInitialDisplayText(initial) {
        if (this.isEmptyInitial(initial)) return "";
        if (typeof initial === "string") return initial;
        return renderLocationLabel(initial);
    }

    locationInitialEquals(a, b) {
        return this.getInitialDisplayText(a) === this.getInitialDisplayText(b);
    }

    syncFromSubmissionInitial(initial, { force = false } = {}) {
        const display = this.getInitialDisplayText(initial);
        if (!force && this.state.wasEdited && display === "" && this.state.value) {
            return;
        }
        if (!force && this.state.value === display && !this.state.wasEdited) {
            return;
        }
        if (display === "") {
            if (!force) {
                if (this.state.value) return;
                if (!this.isEmptyInitial(initial)) return;
            }
            if (force || this.isEmptyInitial(initial)) {
                this.clearLocationField();
            }
            return;
        }
        if (this._resolveTimer) {
            clearTimeout(this._resolveTimer);
            this._resolveTimer = null;
        }
        this._lastScanProcessed = "";
        this.setState({
            value: display,
            wasEdited: false,
            openList: false,
            filtered: [],
            scan: false,
            scanerCanceled: false,
        }, () => {
            this.syncDomScanValue(display);
        });
    }

    shouldAllowBlurResolve() {
        if (this._suppressBlurResolveUntil && Date.now() < this._suppressBlurResolveUntil) {
            return false;
        }
        if (this._manualKeyboardMode || this.state.softFocus) {
            const dom = getTextInputDomNode(this.inputRef);
            if (dom && typeof document !== "undefined" && document.activeElement === dom) {
                return false;
            }
        }
        if (this.isEmptyInitial(this.props.initial)) {
            return false;
        }
        return true;
    }

    isInputFocused() {
        const dom = getTextInputDomNode(this.inputRef);
        return !!(dom && typeof document !== "undefined" && document.activeElement === dom);
    }

    ensureInputFocused() {
        if (!this._mounted || this.isInputFocused()) return;
        const dom = getTextInputDomNode(this.inputRef);
        if (!dom) return;
        try {
            dom.focus({ preventScroll: true });
        } catch (_) {
            dom.focus();
        }
    }

    cancelDeferredBlurResolve() {
        if (this._blurResolveTimer) {
            clearTimeout(this._blurResolveTimer);
            this._blurResolveTimer = null;
        }
    }

    clearLocationField() {
        this._suppressBlurResolveUntil = Date.now() + 2000;
        if (this._resolveTimer) {
            clearTimeout(this._resolveTimer);
            this._resolveTimer = null;
        }
        if (this._refocusTimer) {
            clearTimeout(this._refocusTimer);
            this._refocusTimer = null;
        }
        if (this._blurResolveTimer) {
            clearTimeout(this._blurResolveTimer);
            this._blurResolveTimer = null;
        }
        this._lastScanProcessed = "";
        const dom = getTextInputDomNode(this.inputRef);
        if (dom) {
            try {
                const tracker = dom._valueTracker;
                if (tracker) tracker.setValue("");
                dom.value = "";
            } catch (_) {}
        }
        this.setState({
            value: "",
            wasEdited: false,
            openList: false,
            filtered: [],
            scan: false,
            scanerCanceled: false,
        });
    }

    runDeferredBlurResolve() {
        this.cancelDeferredBlurResolve();
        this._blurResolveTimer = setTimeout(() => {
            this._blurResolveTimer = null;
            if (!this._mounted || !this.shouldAllowBlurResolve()) return;
            if (this.isInputFocused()) return;

            const {
                useScanner,
                scannerInput,
                scannerFieldForInput,
                webHardwareScanner,
                defaultLabel,
                showLocationsSearch,
                isShipmentByLocation,
                softFocus,
                setShowTextBar,
                id,
                position,
            } = this._blurContext || {};

            const value = cleanScannedText(this.getScanInputValue());
            const wasEdited = this.state.wasEdited;
            const resolveExactOnly = this.shouldUseExactLocationMatch();
            const wasManualEntry = this._manualKeyboardMode || this._humanTypingSession || softFocus;

            if (webHardwareScanner) {
                if (value && wasEdited) {
                    if (this.tryResolveScannedValue(value, {
                        exactOnly: resolveExactOnly && this.shouldUseExactOnlyForTypedValue(value),
                    })) return;
                    if (isShipmentByLocation && this.useScannerField() && wasManualEntry) {
                        this.commitUnresolvedLocationText(value, { wasEdited: false });
                        return;
                    }
                    if (!this._manualKeyboardMode && !softFocus) {
                        this.focusVisibleScanField();
                    }
                }
                if (!this.isInputFocused()) {
                    this._manualKeyboardMode = false;
                    this._humanTypingSession = false;
                    this._lastTypeAt = 0;
                }
                return;
            }

            if (!this.isInputFocused()) {
                this._manualKeyboardMode = false;
                this._humanTypingSession = false;
                this._lastTypeAt = 0;
            }

            if (useScanner && scannerInput && scannerFieldForInput) {
                this.setState({ scanerCanceled: false });
                if (softFocus && !this._manualKeyboardMode && !this.isInputFocused()) {
                    this.setState({ softFocus: false });
                }
            }
            if (useScanner && setShowTextBar && isMobileWeb()) {
                setShowTextBar(null, null, null, null, null, null, null, false);
            }
            if (useScanner && value && this.tryResolveScannedValue(value, {
                exactOnly: resolveExactOnly && this.shouldUseExactOnlyForTypedValue(value),
            })) {
                return;
            }
            if (isShipmentByLocation && this.useScannerField()) {
                const { onSelectItem } = this.props;
                if (!value) {
                    if (onSelectItem) onSelectItem("");
                    return;
                }
                this.commitUnresolvedLocationText(value, { wasEdited: false });
                return;
            }
            if (
                (defaultLabel == "toLocation" || showLocationsSearch) &&
                value &&
                wasEdited &&
                this.state.filtered?.length <= 0
            ) {
                this.noFoundLocation(value);
            }
        }, BLUR_RESOLVE_MS);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.openBarCodeScanner != this.props.openBarCodeScanner &&
            nextProps.openBarCodeScanner &&
            this.useScannerField()
        ) {
            this.showScanner(true);
        }
        if (nextProps?.options != this.props.options) {
            this.setState({
                items: nextProps.options || [],
            });
        }
        if (
            nextProps.resetInventorySearch?.changed !== this.props.resetInventorySearch?.changed &&
            nextProps.resetInventorySearch?.position === nextProps.position &&
            this.useScannerField()
        ) {
            this.syncFromSubmissionInitial(nextProps.initial, { force: true });
            return;
        }
        if (nextProps.position != this.props.position) {
            this.setState({
                scan: false,
                wasEdited: false,
                openList: false,
                filtered: [],
                scanerCanceled: false,
            });
            //this.syncFromSubmissionInitial(nextProps.initial, { force: true });
            return;
        }
        const initialChanged = !this.locationInitialEquals(nextProps.initial, this.props.initial);
        const tableRerendered = nextProps.rerenderTable !== this.props.rerenderTable;
        if (initialChanged || tableRerendered) {
            if (this.props.defaultLabel == "toLocation" || this.props.showLocationsSearch) {
                this.syncFromSubmissionInitial(nextProps.initial);
            } else if (typeof nextProps?.initial == "object") {
                this.setState({ locationSelected: nextProps?.initial });
            } else if (typeof nextProps?.initial == "string") {
                this.setState({ value: nextProps.initial, wasEdited: false });
            }
        }
        if (nextProps.closeSearch != this.props.closeSearch) {
            this.setState({ filtered: [], wasEdited: false });
        }
    }
    componentWillUnmount() {
        this._mounted = false;
        if (this._interval) clearTimeout(this._interval);
        if (this._resolveTimer) clearTimeout(this._resolveTimer);
        if (this._refocusTimer) clearTimeout(this._refocusTimer);
        if (this._blurResolveTimer) clearTimeout(this._blurResolveTimer);
        if (this._wedgeIdleTimer) clearTimeout(this._wedgeIdleTimer);
        this.stopFocusWatchdog();
        this.detachDomScanListeners();
        if (this._onWedgeManualKeyboard && typeof window !== "undefined") {
            window.removeEventListener(WEDGE_MANUAL_KB_EVENT, this._onWedgeManualKeyboard);
        }
        stopSymbolWedgeSessionForOwner(this.wedgeOwnerId(), { immediate: true });
    }

    wedgeOwnerId() {
        const { id, position } = this.props;
        return `location-wedge-${id}-${position}`;
    }

    usesWebHardwareScanner() {
        return Platform.OS === "web" && isMobileWeb() && this.useScannerField();
    }

    isManualTypingMode() {
        if (this._humanTypingSession) return true;
        if (this._manualKeyboardMode) return true;
        if (this.usesWebHardwareScanner() && this.state.softFocus) return true;
        if (Platform.OS === "android" && this.state.softFocus && this.useScannerField()) return true;
        return false;
    }

    noteKeyboardInputTiming() {
        const now = Date.now();
        const gap = this._lastTypeAt ? now - this._lastTypeAt : null;
        this._lastTypeAt = now;
        this._suppressBlurResolveUntil = Date.now() + MANUAL_TYPE_DEBOUNCE_MS + 500;
        if (gap != null && gap > 90) {
            this._humanTypingSession = true;
            this.abortPendingWedgeResolve();
        }
    }

    beginManualTypingSession() {
        this._humanTypingSession = true;
        this.abortPendingWedgeResolve();
    }

    shouldDeferManualTypeResolve(text) {
        if (!text) return true;
        // User is still building a split path (e.g. "office/").
        return /[\/\\]$/.test(text);
    }

    shouldUseExactOnlyForTypedValue(text) {
        if (!text) return true;
        return !/[\/\\-]/.test(text);
    }

    runManualTypeResolve() {
        const fromInput = cleanScannedText(this.getScanInputValue());
        const fromState = cleanScannedText(this.state.value);
        const current = fromInput || fromState;
        if (!current) return;

        if (this.shouldDeferManualTypeResolve(current)) {
            this.scheduleResolveInput(current);
            return;
        }

        const exactOnly = this.shouldUseExactOnlyForTypedValue(current);
        if (this.tryResolveScannedValue(current, { exactOnly })) return;

        const { isShipmentByLocation } = this.props;
        if (isShipmentByLocation) {
            this.commitUnresolvedLocationText(current, { wasEdited: false });
        }
    }

    abortPendingWedgeResolve() {
        if (this._wedgeIdleTimer) {
            clearTimeout(this._wedgeIdleTimer);
            this._wedgeIdleTimer = null;
        }
        if (this.usesWebHardwareScanner()) {
            stopSymbolWedgeSessionForOwner(this.wedgeOwnerId(), { immediate: true });
        }
    }

    shouldUseExactLocationMatch() {
        return this.isManualTypingMode();
    }

    getTypeResolveDelayMs() {
        if (this.isManualTypingMode()) return MANUAL_TYPE_DEBOUNCE_MS;
        const { scannerInput } = this.props;
        return scannerInput ? SCAN_RESOLVE_DELAY_MS : 400;
    }

    getScanInputValue(fallbackTarget) {
        if (fallbackTarget && typeof fallbackTarget.value === "string") {
            return normalizeWedgeScanText(fallbackTarget.value);
        }
        const dom = getTextInputDomNode(this.inputRef);
        if (dom && typeof dom.value === "string") {
            return normalizeWedgeScanText(dom.value);
        }
        return normalizeWedgeScanText(this.state.value);
    }

    syncDomScanValue(text) {
        const dom = getTextInputDomNode(this.inputRef);
        if (!dom) return;
        try {
            const tracker = dom._valueTracker;
            if (tracker) tracker.setValue(text);
            dom.value = text;
        } catch (_) {}
    }

    focusVisibleScanField() {
        if (!this.usesWebHardwareScanner() || !this._mounted) return;
        if (this._manualKeyboardMode || this.state.softFocus) {
            this.ensureInputFocused();
            return;
        }
        const focus = () => {
            if (this._manualKeyboardMode || this.state.softFocus) {
                this.ensureInputFocused();
                return;
            }
            inputFocus(this.inputRef);
            const dom = getTextInputDomNode(this.inputRef);
            if (!dom) return;
            try {
                dom.type = "text";
                dom.inputMode = "text";
            } catch (_) {}
            try {
                dom.focus({ preventScroll: true });
            } catch (_) {
                dom.focus();
            }
        };
        focus();
        if (typeof requestAnimationFrame === "function") {
            requestAnimationFrame(focus);
        }
        setTimeout(focus, 0);
    }

    startFocusWatchdog() {
        this.stopFocusWatchdog();
        this._keepWedgeFocusUntil = Date.now() + WEDGE_FOCUS_GUARD_MS;
        this._focusWatchdog = setInterval(() => {
            if (!this._mounted || !this.usesWebHardwareScanner()) {
                this.stopFocusWatchdog();
                return;
            }
            if (Date.now() > this._keepWedgeFocusUntil || this.state.softFocus) {
                this.stopFocusWatchdog();
                return;
            }
            const dom = getTextInputDomNode(this.inputRef);
            if (!dom || typeof document === "undefined") return;
            if (document.activeElement !== dom) {
                this.focusVisibleScanField();
            }
        }, 80);
    }

    stopFocusWatchdog() {
        if (this._focusWatchdog) {
            clearInterval(this._focusWatchdog);
            this._focusWatchdog = null;
        }
    }

    scheduleWedgeIdleComplete() {
        if (!this.usesWebHardwareScanner() || this.isManualTypingMode()) return;
        if (this._wedgeIdleTimer) clearTimeout(this._wedgeIdleTimer);
        this._wedgeIdleTimer = setTimeout(() => {
            this._wedgeIdleTimer = null;
            if (!this._mounted) return;
            const scanned = this.getScanInputValue();
            if (!scanned || scanned.length < 2) return;
            if (isGarbledWedgeScanText(scanned)) return;
            this.processHardwareScan(scanned);
        }, WEDGE_IDLE_MS);
    }

    // Fast-path commit triggered by the scanner's trailing Enter key.
    // Fires after WEDGE_ENTER_MS (120 ms) — instant for normal single-record
    // barcodes.  If the value still ends with "/" at that point the scanner is
    // splitting the barcode across two records; we fall back to the full
    // WEDGE_IDLE_MS window so the second record can arrive.
    scheduleWedgeFastComplete() {
        if (!this.usesWebHardwareScanner() || this.isManualTypingMode()) return;
        if (this._wedgeIdleTimer) clearTimeout(this._wedgeIdleTimer);
        this._wedgeIdleTimer = setTimeout(() => {
            this._wedgeIdleTimer = null;
            if (!this._mounted) return;
            const scanned = this.getScanInputValue();
            if (!scanned || scanned.length < 2) return;
            if (isGarbledWedgeScanText(scanned)) return;
            // Trailing slash means the scanner split the barcode (e.g. "site1/"
            // before sending "bin1" as a second record).  Wait for the rest.
            if (scanned.endsWith("/") || scanned.endsWith("\\")) {
                this.scheduleWedgeIdleComplete();
                return;
            }
            this.processHardwareScan(scanned);
        }, WEDGE_ENTER_MS);
    }

    enableManualKeyboard() {
        if (!this.usesWebHardwareScanner()) return;
        this._manualKeyboardMode = true;
        this._humanTypingSession = true;
        this.stopFocusWatchdog();
        this._keepWedgeFocusUntil = 0;
        if (this._wedgeIdleTimer) {
            clearTimeout(this._wedgeIdleTimer);
            this._wedgeIdleTimer = null;
        }
        this._suppressBlurResolveUntil = Date.now() + MANUAL_TYPE_DEBOUNCE_MS + 500;
        this.cancelDeferredBlurResolve();
        const dom = getTextInputDomNode(this.inputRef);
        if (dom) {
            try {
                dom.inputMode = "text";
            } catch (_) {}
        }
        if (!this.state.softFocus) {
            this.setState({ softFocus: true });
        }
    }

    armWedgeFromGesture() {
        if (!this.usesWebHardwareScanner()) return;
        if (this._manualKeyboardMode || this.state.softFocus) return;
        startSymbolWedgeSession(
            null,
            null,
            this.wedgeOwnerId(),
            () => this.getScanInputValue(),
            { useHiddenCapture: false }
        );
        extendSymbolScanEnterShield(8000);
        this._suppressBlurResolveUntil = Date.now() + 3000;
        if (!this.state.softFocus) {
            this.startFocusWatchdog();
        }
        this.focusVisibleScanField();
        this.attachDomScanListeners();
    }

    startWedgeSession() {
        this.armWedgeFromGesture();
    }

    onDomPreScanPointer() {
        if (this._manualKeyboardMode || this.state.softFocus) return;
        this.armWedgeFromGesture();
    }

    processHardwareScan(raw) {
        if (this.isManualTypingMode()) return;
        const clean = normalizeWedgeScanText(raw);
        if (!clean || !this._mounted) return;
        if (isGarbledWedgeScanText(clean)) return;

        const now = Date.now();
        if (clean === this._lastScanProcessed && now - (this._lastScanProcessedAt || 0) < 200) {
            return;
        }
        this._lastScanProcessed = clean;
        this._lastScanProcessedAt = now;
        this._humanTypingSession = false;
        this._lastTypeAt = 0;

        if (this._wedgeIdleTimer) {
            clearTimeout(this._wedgeIdleTimer);
            this._wedgeIdleTimer = null;
        }
        if (this._resolveTimer) {
            clearTimeout(this._resolveTimer);
            this._resolveTimer = null;
        }
        this._keepWedgeFocusUntil = Date.now() + 5000;
        extendSymbolScanEnterShield(8000);

        this.setState({ value: clean, wasEdited: true }, () => {
            if (!this._mounted) return;
            this.syncDomScanValue(clean);
            if (!this.tryResolveScannedValue(clean)) {
                if (this.props.isShipmentByLocation || this.props.isInventoryTransfer) {
                    this.searchItems(clean, false);
                }
            }
            extendSymbolScanEnterShield(8000);
            if (!this.state.softFocus) {
                setTimeout(() => this.focusVisibleScanField(), 50);
            }
        });
    }

    finishWedgeEnterScan(target) {
        if (this.isManualTypingMode()) return;
        setTimeout(() => {
            if (!this._mounted) return;
            const scanned = this.getScanInputValue(target);
            if (scanned && !isGarbledWedgeScanText(scanned)) {
                this.processHardwareScan(scanned);
            } else {
                this.scheduleWedgeIdleComplete();
            }
        }, 40);
    }

    onDomEnterBlock(e) {
        if (!this.usesWebHardwareScanner() || this.isManualTypingMode()) return;
        const isEnter = e.key === "Enter" || e.key === "Return" || e.keyCode === 13;
        const isTab = e.key === "Tab" || e.keyCode === 9;
        if (!isEnter && !isTab) return;
        e.preventDefault();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        if (e.stopPropagation) e.stopPropagation();
        extendSymbolScanEnterShield(8000);
        this.finishWedgeEnterScan(e.target);
    }

    handleWedgeDomInput(e) {
        this.noteKeyboardInputTiming();
        if (this.isManualTypingMode()) return;
        const raw = e?.target?.value;
        if (!raw) return;
        const hasTerminator = /[\r\n\t]/.test(raw);
        const clean = cleanScannedText(parseScannedValue(raw));
        if (!clean || isGarbledWedgeScanText(clean)) return;
        if (hasTerminator) {
            this.processHardwareScan(clean);
            return;
        }
        this.setState({ value: clean, wasEdited: true });
        this.scheduleWedgeIdleComplete();
    }

    attachDomScanListeners() {
        if (!this.usesWebHardwareScanner()) return;

        const dom = getTextInputDomNode(this.inputRef);
        if (!dom || dom === this._wedgeDom) return;
        this.detachDomScanListeners();
        this._wedgeDom = dom;

        this._onDomPreScanPointer = () => this.onDomPreScanPointer();
        this._onDomWedgeInput = (e) => this.handleWedgeDomInput(e);
        this._onDomEnterBlock = (e) => this.onDomEnterBlock(e);
        this._onDomBeforeInput = (e) => {
            if (this.isManualTypingMode()) return;
            if (e.inputType === "insertLineBreak" || e.data === "\n" || e.data === "\r") {
                e.preventDefault();
                extendSymbolScanEnterShield(8000);
                // Fast path: commit after WEDGE_ENTER_MS (120 ms).
                // scheduleWedgeFastComplete automatically falls back to the full
                // WEDGE_IDLE_MS window when a trailing "/" signals a split barcode.
                this.scheduleWedgeFastComplete();
            }
        };
        dom.addEventListener("pointerdown", this._onDomPreScanPointer, true);
        dom.addEventListener("touchstart", this._onDomPreScanPointer, true);
        dom.addEventListener("input", this._onDomWedgeInput, true);
        dom.addEventListener("keydown", this._onDomEnterBlock, true);
        dom.addEventListener("beforeinput", this._onDomBeforeInput, true);
        try {
            dom.type = "text";
            dom.setAttribute("enterkeyhint", "done");
            dom.inputMode = "text";
        } catch (_) {}
    }

    detachDomScanListeners() {
        if (!this._wedgeDom) return;
        if (this._onDomPreScanPointer) {
            this._wedgeDom.removeEventListener("pointerdown", this._onDomPreScanPointer, true);
            this._wedgeDom.removeEventListener("touchstart", this._onDomPreScanPointer, true);
        }
        if (this._onDomWedgeInput) {
            this._wedgeDom.removeEventListener("input", this._onDomWedgeInput, true);
        }
        if (this._onDomEnterBlock) {
            this._wedgeDom.removeEventListener("keydown", this._onDomEnterBlock, true);
        }
        if (this._onDomBeforeInput) {
            this._wedgeDom.removeEventListener("beforeinput", this._onDomBeforeInput, true);
        }
        this._wedgeDom = null;
        this._onDomPreScanPointer = null;
        this._onDomWedgeInput = null;
        this._onDomEnterBlock = null;
        this._onDomBeforeInput = null;
    }

    getLocationOptions() {
        const { options } = this.props;
        const { items } = this.state;
        if (Array.isArray(options) && options.length) return options;
        if (Array.isArray(items) && items.length) return items;
        return [];
    }

    shouldAutoOpenCameraOnFocus() {
        const { scannerInput, isShipmentByLocation, showLocationsSearch, defaultLabel } = this.props;
        /*
         * Shipment-by-location table rows: never auto-open the camera on focus.
         * Row 1 often already has a value (so camera stayed off); empty row 2 used to
         * auto-open the camera on Zebra / mobile web and crash the app. Use the hardware
         * wedge or the KeyboardBar scan button instead.
         */
        if (isShipmentByLocation && showLocationsSearch && defaultLabel === "location") {
            return false;
        }
        if (!scannerInput || !this.scannerFieldActive()) return false;
        if (Platform.OS === "android") return false;
        return Platform.OS === "ios" || (Platform.OS === "web" && isMobileWeb());
    }

    showScanAlert(message) {
        if (Platform.OS === "web") {
            Swal.fire({
                text: message,
                confirmButtonText: "Accept",
                confirmButtonColor: colors.orange,
                backdrop: false,
            });
            return;
        }
        Alert.alert("", message, [{ text: "Accept" }]);
    }

    handleUnresolvedShipmentScan(raw) {
        this.commitUnresolvedLocationText(raw, { wasEdited: true });
    }

    commitUnresolvedLocationText(raw, { wasEdited = false, updateForm = true } = {}) {
        const { onSelectItem, isShipmentByLocation } = this.props;
        const clean = parseScannedValue(raw).replace(/[\x00-\x1F\x7F]/g, "").trim();
        if (!clean || !this._mounted) return;
        const hideList = this.useScannerField() && isShipmentByLocation;
        this.setState({
            value: clean,
            wasEdited,
            openList: hideList ? false : true,
            filtered: [],
            scan: false,
        }, () => {
            this.syncDomScanValue(clean);
            if (!hideList) {
                this.searchItems(clean, false);
            }
            if (updateForm && onSelectItem) {
                onSelectItem(clean);
            }
        });
    }

    useScannerField() {
        const { isShipmentByLocation, showLocationsSearch, defaultLabel, isInventoryTransfer } = this.props;
        if (isInventoryTransfer && (defaultLabel === "fromLocation" || defaultLabel === "toLocation")) {
            return true;
        }
        return !!(isShipmentByLocation && showLocationsSearch && defaultLabel === "location");
    }

    scannerFieldActive() {
        const { showScanner, scannerInput } = this.props;
        return showScanner || (this.useScannerField() && scannerInput);
    }

    showScanner(value) {
        this.setState({ scan: value, scanerCanceled: value ? false : this.state.scanerCanceled });
    }

    showScannerInput() {
        const { setFormScroll } = this.props;
        if (!this.state.scanerCanceled) {
            if (setFormScroll) setFormScroll(false);
            setTimeout(() => {
                this.showScanner(true);
                setTimeout(() => {
                    if (setFormScroll) setFormScroll(true);
                }, 300);
            }, 500);
        } else {
            this.setState({ scanerCanceled: false });
        }
    }

    canShowCameraButton() {
        const { defaultComponent, scannerInput, showScanner } = this.props;
        if (!this.useScannerField()) return false;
        if (defaultComponent) return false;
        if (!(showScanner || scannerInput)) return false;
        if (Platform.OS === "ios") return false;
        return Platform.OS !== "web" || isMobileWeb();
    }

    useCompactMobileLayout() {
        const { isShipmentByLocation } = this.props;
        if (!this.useScannerField() || !isShipmentByLocation) return false;
        return Platform.OS === "android" || (Platform.OS === "web" && isMobileWeb());
    }

    openCameraScanner() {
        //const { setFormScroll, setShowTextBar } = this.props;
        //if (this.usesWebHardwareScanner()) {
        //    this.stopFocusWatchdog();
        //    this._keepWedgeFocusUntil = 0;
        //    this._manualKeyboardMode = false;
        //    stopSymbolWedgeSessionForOwner(this.wedgeOwnerId(), { immediate: true });
        //}
        //if (setFormScroll) setFormScroll(false);
        //if (setShowTextBar && (isMobileWeb() || Platform.OS === "android")) {
        //    setShowTextBar(null, null, null, null, null, null, null, false);
        //}
        this.showScanner(true);
    }

    findLocationByScan(data, { exactOnly = false } = {}) {
        return findLocationInList(data, this.getLocationOptions(), { exactOnly });
    }

    tryResolveScannedValue(raw, { updateForm = true, exactOnly = false } = {}) {
        const clean = parseScannedValue(raw).replace(/[\x00-\x1F\x7F]/g, "").trim();
        if (!clean) return false;
        const match = this.findLocationByScan(clean, { exactOnly });
        if (match) {
            this.selectLocation(match, { skipForm: !updateForm });
            return true;
        }
        return false;
    }

    scheduleResolveInput(raw) {
        const useScanner = this.useScannerField();
        if (!useScanner) return;

        if (this._resolveTimer) clearTimeout(this._resolveTimer);

        this._resolveTimer = setTimeout(() => {
            this._resolveTimer = null;
            if (!this._mounted) return;
            this.runManualTypeResolve();
        }, MANUAL_TYPE_DEBOUNCE_MS);
    }

    finishScanProcessing(data) {
        if (!this._mounted || !data) return;
        try {
            var location = this.findLocationByScan(data);
            if (location) {
                this.selectLocation(location);
                return;
            }
            if (this.useScannerField() && this.props.isShipmentByLocation) {
                this.commitUnresolvedLocationText(data);
                return;
            }
        } catch (err) {
            console.warn("SearchInventoryLocations finishScanProcessing:", err);
            if (!(this.useScannerField() && this.props.isShipmentByLocation)) {
                this.showScanAlert("Unable to process the scanned location. Please try again or search manually.");
            }
        }
    }

    handleBarCodeScanned(raw) {
        const data = parseScannedValue(raw);
        if (!this._mounted || this._handlingScan) return;
        this._handlingScan = true;
        this.setState({ scan: false });
        const settleMs = Platform.OS === "web" ? 450 : 250;
        setTimeout(() => {
            this._handlingScan = false;
            if (!this._mounted) return;
            this.finishScanProcessing(data);
        }, settleMs);
    }
    searchItems(val, focused, { autoSelect = null } = {}) {
        const items = this.getLocationOptions();
        const { isShipmentByLocation } = this.props;
        const hideList = this.useScannerField() && isShipmentByLocation;
        const allowAutoSelect = autoSelect === null
            ? (isShipmentByLocation && !this.useScannerField() && !this.isManualTypingMode())
            : autoSelect;
        var search = val;
        if (this._interval) clearTimeout(this._interval);
        if (search) {
            search = search.toLowerCase().trim();
            if (isShipmentByLocation && allowAutoSelect) {
                var resolved = this.findLocationByScan(search);
                if (resolved) {
                    this.selectLocation(resolved);
                    return;
                }
            }
            var filtered = items.filter((item) => locationListIncludesTerm([item], search));
            if (isShipmentByLocation) filtered = filtered.slice(0, 10);
            this.setState({ filtered: hideList ? [] : filtered, openList: hideList ? false : true });

        } else {
            var filtered = [];
            if (isShipmentByLocation) filtered = items.slice(0, 10);
            else filtered = items;
            this.setState({ filtered: hideList ? [] : filtered, wasEdited: focused ? true : false, openList: hideList ? false : true });
        }
    }
    noFoundLocation(search) {
        const { onSelectItem } = this.props;
        search = search.toLowerCase().trim();
        var filtered = this.state.items.filter((item) => {
            var completeLocation = "";
            if (item?.site?.locationName) {
                completeLocation += item.site.locationName;
            }
            if (item?.zone?.locationName) {
                completeLocation += "-" + item.zone.locationName;
            }
            if (item?.aisle?.locationName) {
                completeLocation += "-" + item.aisle.locationName;
            }
            if (item?.rackBay?.locationName) {
                completeLocation += "-" + item.rackBay.locationName;
            }
            if (item?.shelfLevel?.locationName) {
                completeLocation += "-" + item.shelfLevel.locationName;
            }
            if (item?.bin?.locationName) {
                completeLocation += "-" + item.bin.locationName;
            }
            completeLocation = completeLocation.toLowerCase().trim();
            return completeLocation.includes(search);
        });
        if (filtered.length <= 0)
            Swal.fire({
                text: "This location does not exist. A new main location will be created automatically on sending the form, but it must be reorganized by an administrator. This is not recommended. Do you want to proceed?",
                confirmButtonText: "Yes",
                cancelButtonText: "No",
                showCancelButton: true,
                confirmButtonColor: colors.orange,
                backdrop: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    this.setState({ value: search, filtered: [], openList: false });
                    onSelectItem(search);
                } else {
                    this.setState({ value: "", filtered: [], openList: false });
                    onSelectItem("");
                }
            });
    }
    selectLocation(item, { skipForm = false } = {}) {
        const { onSelectItem } = this.props;
        const label = renderLocationLabel(item);
        if (this._resolveTimer) {
            clearTimeout(this._resolveTimer);
            this._resolveTimer = null;
        }
        this.setState({
            value: label,
            wasEdited: false,
            openList: false,
            filtered: [],
            scan: false,
        }, () => {
            if (skipForm || !onSelectItem || !this._mounted || item == null || item === "") return;
            setTimeout(() => {
                if (!this._mounted) return;
                if (typeof item === "string") {
                    onSelectItem(item);
                } else {
                    onSelectItem([item]);
                }
            }, 50);
        });
    }

    renderLocation(location) {
        return renderLocationLabel(location);
    }
    render() {
        const { value, items, wasEdited, filtered, openList, scan, scanerCanceled, softFocus, inputAccessoryViewID } = this.state;
        const { defaultComponent, onSelectItem, placeholder, customers, width, isRecipe, openDropDown, backgroundColor, isLast, defaultLabel, isDropDownOpened, resetDropDown, onFocus, setShowTextBar, showLocationsSearch, isShipmentByLocation, handleFocusOnElement, setFormScroll, id, position, scannerInput, showScanner } = this.props;
        const useScanner = this.useScannerField();
        const scannerFieldForInput = this.scannerFieldActive();
        const webHardwareScanner = this.usesWebHardwareScanner();
        const showCameraButton = this.canShowCameraButton();
        const compactMobile = this.useCompactMobileLayout();

        const scanInput = (
            <TextInput
                {...(webHardwareScanner ? {} : getNoAutofillProps())}
                ref={(el) => {
                    if (webHardwareScanner) {
                        scannerFieldRef(this.inputRef)(el);
                    } else {
                        noAutofillRef(this.inputRef)(el);
                    }
                    if (el) {
                        setTimeout(() => this.attachDomScanListeners(), 0);
                    }
                }}
                value={value}
                editable={!defaultComponent && !(useScanner && scannerInput && scannerFieldForInput && !value && !scanerCanceled && Platform.OS === "ios")}
                showSoftInputOnFocus={!defaultComponent}
                blurOnSubmit={webHardwareScanner ? false : undefined}
                returnKeyType={webHardwareScanner ? "done" : undefined}
                onPressIn={webHardwareScanner && !softFocus ? () => this.armWedgeFromGesture() : undefined}
                onMouseDown={webHardwareScanner && !softFocus ? () => this.armWedgeFromGesture() : undefined}
                onLongPress={webHardwareScanner ? () => this.enableManualKeyboard() : undefined}
                onPress={() => {
                    if (this.shouldAutoOpenCameraOnFocus() && useScanner && !value && !scanerCanceled) {
                        this.showScannerInput();
                    }
                    if (Platform.OS === "web" && isMobileWeb() && !defaultComponent && onFocus) {
                        onFocus();
                    }
                }}
                onFocus={() => {
                    this.cancelDeferredBlurResolve();
                    this._suppressBlurResolveUntil = Date.now() + MANUAL_TYPE_DEBOUNCE_MS + 500;
                    if (webHardwareScanner) {
                        this._manualKeyboardMode = true;
                        this.enableManualKeyboard();
                    }
                    if (useScanner && scannerFieldForInput) {
                        this.beginManualTypingSession();
                    }
                    if (this.shouldAutoOpenCameraOnFocus() && useScanner && !value && !scanerCanceled) {
                        this.showScannerInput();
                        if (Platform.OS !== "ios" && !softFocus) {
                            this.setState({ softFocus: true });
                            return;
                        }
                    }
                    if (useScanner && Platform.OS === "android" && scannerInput && scannerFieldForInput && !softFocus) {
                        this.setState({ softFocus: true });
                    }
                    const skipParentScannerReopen = useScanner && scannerInput && scannerFieldForInput && !value && scanerCanceled;
                    if (onFocus && !skipParentScannerReopen && !webHardwareScanner) {
                        onFocus();
                    } else if (skipParentScannerReopen || webHardwareScanner) {
                        setTimeout(() => {
                            if (!this._mounted || (webHardwareScanner && !this.isInputFocused())) return;
                            if (handleFocusOnElement && this.inputRef?.current) {
                                handleFocusOnElement(this.inputRef.current);
                            }
                        }, 350);
                    }
                    if (!defaultComponent && !value && !(useScanner && scannerFieldForInput)) {
                        this.searchItems("", true);
                    }
                    if (
                        useScanner &&
                        setShowTextBar &&
                        isMobileWeb() &&
                        !webHardwareScanner &&
                        !this.useScannerField()
                    ) {
                        setShowTextBar(id, position, [], null, null, null, null, false, true);
                    } else if (
                        setShowTextBar &&
                        (isMobileWeb() || Platform.OS === "android") &&
                        !webHardwareScanner &&
                        !this.useScannerField()
                    ) {
                        setShowTextBar(null, null, null, null, null, null, null, false);
                    }
                }}
                onContentSizeChange={(event) => {
                    if (compactMobile) return;
                    if (event.nativeEvent.contentSize.height == 0) {
                        if (this.props.placeholder == '') {
                            this.setState({ height: 30 })
                        }
                        this.setState({ height: event.nativeEvent.contentSize.height });
                    } else {
                        setTimeout(() => {
                            this.setState({ height: event.nativeEvent.contentSize.height });
                        }, 5)
                    }
                }}
                onBlur={() => {
                    this._blurContext = {
                        useScanner,
                        scannerInput,
                        scannerFieldForInput,
                        webHardwareScanner,
                        defaultLabel,
                        showLocationsSearch,
                        isShipmentByLocation,
                        softFocus,
                        setShowTextBar,
                        id,
                        position,
                    };
                    this.runDeferredBlurResolve();
                }}
                inputAccessoryViewID={useScanner ? inputAccessoryViewID : undefined}
                style={compactMobile ? {
                    ...styles.compactInput,
                    fontStyle: this.state.value ? this.state.value.length == 0 ? 'italic' : 'normal' : 'normal',
                } : {
                    ...styles.input,
                    ...(showCameraButton ? styles.inputWithCamera : null),
                    borderColor: "#e7e7e7",
                    borderRadius: 5,
                    borderWidth: 1,
                    color: "#424242",
                    fontFamily: "Roboto",
                    fontSize: 20,
                    fontWeight: "400",
                    padding: 5,
                    height: this.state.height,
                    fontStyle: this.state.value ? this.state.value.length == 0 ? 'italic' : 'normal' : 'normal',
                    overflow: 'hidden'
                }}
                multiline={compactMobile ? false : !webHardwareScanner}
                placeholder={placeholder}
                placeholderTextColor={'rgb(0, 47, 108)'}
                onChangeText={(val) => {
                    const raw = parseScannedValue(val);
                    const hasTerminator = /[\r\n\t]/.test(raw);
                    const cleanVal = cleanScannedText(raw);

                    if (webHardwareScanner && hasTerminator && cleanVal && !this.isManualTypingMode()) {
                        this.processHardwareScan(cleanVal);
                        return;
                    }

                    if (webHardwareScanner && !softFocus && !this._manualKeyboardMode && !this._humanTypingSession) {
                        if (cleanVal && !isGarbledWedgeScanText(cleanVal)) {
                            this.setState({ value: cleanVal, wasEdited: true });
                        }
                        this.scheduleWedgeIdleComplete();
                        return;
                    }

                    this.noteKeyboardInputTiming();

                    if (useScanner && this.scannerFieldActive()) {
                        this.beginManualTypingSession();
                    }

                    if (cleanVal == '' && this.props.placeholder == '') {
                        this.setState({ value: cleanVal, height: 30, wasEdited: true, openList: false, filtered: [] });
                    } else {
                        const hideListWhileTyping = useScanner && this.scannerFieldActive() && isShipmentByLocation;
                        this.setState({
                            value: cleanVal,
                            wasEdited: true,
                            ...(hideListWhileTyping ? { openList: false, filtered: [] } : null),
                        });
                    }

                    if (useScanner && this.scannerFieldActive()) {
                        this.scheduleResolveInput(cleanVal);
                        return;
                    }

                    if (this._interval) clearTimeout(this._interval);
                    const searchDelay = this.isManualTypingMode()
                        ? MANUAL_TYPE_DEBOUNCE_MS
                        : TYPE_SEARCH_DEBOUNCE_MS;
                    this._interval = setTimeout(() => {
                        this._interval = null;
                        this.searchItems(cleanVal, false, {
                            autoSelect: this.useScannerField() ? false : !this.isManualTypingMode(),
                        });
                    }, searchDelay);
                    if (!this.useScannerField()) {
                        onSelectItem(cleanVal);
                    }
                }}
            />
        );

        const cameraButton = showCameraButton ? (
            <TouchableOpacity
                onPress={() => this.openCameraScanner()}
                style={compactMobile ? styles.compactCameraBtn : styles.cameraBtn}
                accessibilityLabel="Scan location with camera"
            >
                <MaterialIcons name="photo-camera" color="#fff" size={compactMobile ? 20 : 24} />
            </TouchableOpacity>
        ) : null;

        return <View style={{ ...styles.container, ...(compactMobile ? styles.containerCompact : null), paddingTop: width ? 0 : 5 }}>
            {compactMobile ? (
                <View style={styles.compactFieldShell}>
                    {scanInput}
                    {cameraButton}
                </View>
            ) : (
                <View style={showCameraButton ? styles.inputRow : undefined}>
                    {scanInput}
                    {cameraButton}
                </View>
            )}
            {Platform.OS === "ios" && useScanner && (
                <InputAccessoryView nativeID={inputAccessoryViewID}>
                    <KeyboardBar
                        abcOptionPress={null}
                        scanOptionPress={() => this.showScanner(true)}
                    />
                </InputAccessoryView>
            )}
            {
                items?.length > 0 && defaultLabel != "toLocation" && !showLocationsSearch &&
                <DropDownLocations
                    initialOption={this.state.locationSelected}
                    updated={this.state.locationSelected}
                    options={filtered}
                    defaultComponent={defaultComponent}
                    openList={openList}
                    allLabel={"All Locations"}
                    onSelect={(option) => {
                        onSelectItem(option)
                    }}
                    width={width}
                    isRecipe={isRecipe}
                    openDropDown={openDropDown}
                    backgroundColor={backgroundColor}
                    isLast={isLast}
                    isDropDownOpened={isDropDownOpened}
                    resetDropDown={resetDropDown}
                    showScanner={showScanner}
                />
            }
            {
                filtered?.length > 0 && wasEdited && (defaultLabel == "toLocation") &&
                <View style={styles.listContainer}>
                    {filtered.map((item, idx) => (
                        <TouchableOpacity
                            key={idx}
                            style={styles.row}
                            onPress={() => this.selectLocation(item)}
                        >
                            <View style={styles.rowText}>
                                <Text style={styles.rowLabel}>{this.renderLocation(item)}</Text>
                            </View>
                            <TouchableOpacity
                                style={styles.rowBtn}
                                onPress={() => this.selectLocation(item)}
                            >
                                <MaterialCommunityIcons name="plus" size={22} color={colors.selectSearch} />
                            </TouchableOpacity>
                        </TouchableOpacity>
                    ))}
                </View>
            }
            {
                (value && filtered?.length <= 0 && wasEdited && !(showLocationsSearch && isShipmentByLocation && useScanner)) ? <View>
                    <Text>No locations were found</Text>
                </View> : null
            }
            {scan && useScanner ? (
                <BarcodeScannerComponent
                    onBarCodeScanned={(data) => this.handleBarCodeScanned(data)}
                    closeScanner={() => {
                        this.showScanner(false);
                        const { setFormScroll } = this.props;
                        if (setFormScroll) setFormScroll(true);
                        if (scannerInput && scannerFieldForInput) {
                            this.setState({ scanerCanceled: true }, () => {
                                setTimeout(() => {
                                    if (!this._mounted) return;
                                    if (webHardwareScanner) {
                                        this.armWedgeFromGesture();
                                    } else {
                                        inputFocus(this.inputRef);
                                    }
                                }, 500);
                            });
                        }
                    }}
                />
            ) : null}
        </View>
    }
}

export default connect((state, ownProps = {}) => ({
    isLoading: state.forms.getIn(["form", "status"]) === "loading",
    company_id: state.user.get("company_id"),
}), (dispatch, props) => ({
    searchItems: (formid, saveAsTemplate, saveAsForm, callback) =>
        dispatch(searchItems(formid, saveAsTemplate, saveAsForm, callback)),
    getMyCustomers: (data, callback) =>
        dispatch(getMyCustomers(data, callback)),
    getMySuppliers: (id, page, filter, callback) =>
        dispatch(getMySuppliers(id, page, filter, callback)),
}))(SearchInventoryLocations);

const styles = StyleSheet.create({
    container: {
        flexDirection: "column",
        paddingTop: 5,
        flex: 1,
    },
    containerCompact: {
        flex: 0,
        width: "100%",
        alignSelf: "stretch",
        paddingTop: 0,
    },
    compactFieldShell: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        minHeight: 42,
        borderWidth: 1,
        borderColor: "#e7e7e7",
        borderRadius: 5,
        backgroundColor: "#fff",
        overflow: "hidden",
    },
    compactInput: {
        flex: 1,
        minWidth: 0,
        borderWidth: 0,
        color: "#424242",
        fontFamily: "Roboto",
        fontSize: 16,
        fontWeight: "400",
        paddingHorizontal: 8,
        paddingVertical: 8,
        height: 42,
        backgroundColor: "transparent",
    },
    compactCameraBtn: {
        width: 40,
        height: 40,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.primary,
        flexShrink: 0,
        marginRight: 1,
        borderRadius: 4,
    },
    inputRow: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
    },
    inputWithCamera: {
        flex: 1,
        marginRight: 8,
    },
    cameraBtn: {
        width: 44,
        height: 44,
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 22,
        backgroundColor: colors.primary,
        flexShrink: 0,
    },
    input: {
        borderColor: "#e7e7e7",
        borderRadius: 5,
        borderWidth: 1,
        color: "#424242",
        fontFamily: "Roboto",
        fontSize: 22,
        fontWeight: "400",
        paddingLeft: 10,
    },
    label: {
        fontSize: 20,
        color: '#939598',
        fontWeight: '300',
        fontFamily: 'Roboto',

    },
    listContainer: {
        paddingTop: 4,
        width: "100%",
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        paddingVertical: 6,
        paddingHorizontal: 4,
        borderBottomWidth: 1,
        borderBottomColor: "#e7e7e7",
    },
    rowText: {
        flex: 1,
        paddingRight: 8,
    },
    rowLabel: {
        fontFamily: "Roboto",
        fontSize: 18,
        color: "#424242",
    },
    rowBtn: {
        borderRadius: 4,
        padding: 6,
        borderWidth: 1,
        borderColor: colors.selectSearch,
        alignItems: "center",
        justifyContent: "center",
        minWidth: 36,
        minHeight: 36,
    },
});